import { FC } from "react";
import styled from "styled-components";
import { color, fonts } from "@styles";
import { H1 } from "../../atoms/Heading";
import { P1 } from "../../atoms/Paragraph";
import {
  getStrapiImageByBreakpoint,
  formatPublishedDate,
  getReadMinutesFromHtml,
  formatHtml,
  ContentType,
} from "@utils";
import { PillSection } from "components/molecules/PillSection";
import { Pill } from "components/atoms/Pill";
import { BlogShareIcon } from "components/blog/BlogShareIcon";
import { mobile, tablet } from "components/templates/Breakpoints";
import Image from "next/image";
import { Spacer } from "components/templates/Spacer";
import ResourcesBreadcrumb from "./ResourcesBreadcrumb";
import { Category, LocationInfo, PostData, Tags } from "./types";
import FloatingSubscriptionPopup from "components/organisms/FloatingSubscriptionPopup";
import { YuLifeLocale } from "@hooks";

const setLocationInfo = (contentType: ContentType, tags: Tags, category: Category) => {
  if (contentType === ContentType.blog) {
    const { name: categoryName, slug } = tags?.data[0]?.attributes || {};
    return { categoryName, slug, contentType };
  }

  const { name: categoryName, slug } = category?.data?.attributes || {};
  return { categoryName, slug, contentType };
};

export const ResourceArticleBody: FC<
  React.PropsWithChildren<{
    postData?: PostData;
    contentType?: ContentType;
    locale: YuLifeLocale;
  }>
> = ({ postData, contentType, locale }) => {
  if (!postData) {
    return null;
  }

  const { title, author, featuredImage, excerpt, post, publishDate, slug, category, tags } =
    postData;

  let html = formatHtml(post);

  const isBlog = contentType === ContentType.blog;
  const locationInfo: LocationInfo = setLocationInfo(contentType, tags, category);

  return (
    <>
      <Section>
        <Spacer height={["1rem", "1rem", "1.75rem"]} />
        <>
          <ResourcesBreadcrumb pageType="resource" locationInfo={locationInfo} locale={locale} />
          <Spacer height={["5rem", "5rem", "1rem"]} />
        </>
        {featuredImage?.data ? (
          <HeroImage
            src={getStrapiImageByBreakpoint(featuredImage?.data?.attributes?.formats, "xxlarge")}
            alt={featuredImage?.data?.attributes?.caption || ""}
            width={1128 * 2}
            height={473 * 2}
            quality={100}
            loading="eager"
          />
        ) : null}
      </Section>
      <Spacer height={["2rem", "2rem", "1.5rem"]} />
      {locationInfo.categoryName ? (
        <PillSection isCentered>
          {!isBlog && tags.data.length > 0 && (
            <Pill color="purple" text={tags.data[0]?.attributes?.name} />
          )}
          <Pill color={isBlog ? "purple" : "grey"} text={locationInfo.categoryName} />
          {postData.post ? getReadMinutesFromHtml(postData.post, locale) : null}
        </PillSection>
      ) : null}
      <HeaderSection>
        <Title>{title}</Title>
        <Subtitle bold>{excerpt}</Subtitle>
      </HeaderSection>
      {locale !== "jp" && <FloatingSubscriptionPopup location={"dynamic"} />}
      <ArticleInfoSection>
        {author?.data?.attributes?.image?.data?.attributes?.formats && (
          <BlogAvatarContainer>
            <BlogAvatar
              src={getStrapiImageByBreakpoint(
                author.data.attributes.image.data.attributes.formats,
                "medium",
              )}
            />
          </BlogAvatarContainer>
        )}
        <PublishedInfo>
          {author?.data?.attributes?.name ? `${author.data.attributes.name} - ` : ""}
          {formatPublishedDate(publishDate, locale)}
        </PublishedInfo>
        <StyledBlogShareIcon title={title} slug={`${slug}/`} contentType="article" />
      </ArticleInfoSection>
      <ArticleSection>
        <ArticleStyles $locale={locale} dangerouslySetInnerHTML={{ __html: html }} />
        <StyledBlogShareIcon title={title} slug={`${slug}/`} contentType="article" textVersion />
        {author.data && locale !== "jp" && (
          <BlogAuthorCard>
            <BlogAuthorWrapper>
              {author?.data?.attributes?.image?.data?.attributes?.formats && (
                <BlogAvatar
                  src={getStrapiImageByBreakpoint(
                    author.data.attributes.image.data.attributes.formats,
                    "medium",
                  )}
                />
              )}
              <AuthorName bold>{author.data.attributes.name}</AuthorName>
            </BlogAuthorWrapper>
            <P1 pt="0">{author.data.attributes.bio}</P1>
          </BlogAuthorCard>
        )}
      </ArticleSection>
    </>
  );
};

const StyledBlogShareIcon = styled(BlogShareIcon)`
  transform: translateY(1.6rem);
`;

const Section = styled.section`
  margin: 0 auto;
  max-width: 76.5rem;
  padding: 0 3rem;

  ${mobile} {
    padding: 0 1rem;
  }
`;

const HeroImage = styled(Image)`
  height: auto;
  width: 100%;
  max-height: 41.5rem;
  border-radius: 0.5rem;
  border: 1px solid ${color.B240};
  object-fit: cover;
`;

const ArticleSection = styled.div`
  margin: 0 auto;
  width: 100%;
  max-width: 72rem;
  padding: 0 9.5rem 5rem;

  ${tablet} {
    padding: 0rem 6rem;
  }

  ${mobile} {
    padding: 0rem 1.5rem;
  }
`;

const HeaderSection = styled.div`
  max-width: 63rem;
  margin: 0 auto;
  padding: 2rem 2rem 0rem;

  ${tablet} {
    padding: 2rem 3rem 0rem 3rem;
  }

  ${mobile} {
    padding: 1.5rem 1.5rem 0rem 1.5rem;
  }
`;

const ArticleInfoSection = styled.div`
  display: flex;
  padding-bottom: 5rem;
  justify-content: center;
  gap: 1.5rem;
  align-items: center;

  ${mobile} {
    gap: 1rem;
    margin-top: 1rem;
    padding-bottom: 2.5rem;
  }

  @media only screen and (max-width: 25rem) {
    gap: 0.5rem;
    flex-direction: column;
  }
`;

const PublishedInfo = styled(P1)`
  color: ${color.B2100};

  ${mobile} {
    padding-top: 1rem;
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;

const ArticleStyles = styled.div<{ $locale: YuLifeLocale }>`
  a {
    color: ${color.P100} !important;
    text-decoration: underline;
  }

  strong,
  span {
    font-family: inherit;
  }

  p,
  li {
    position: relative;
    text-decoration: inherit;
    font-style: normal;
    font-weight: 400;
    font-size: 1.25rem;
    line-height: 2rem;
    letter-spacing: 0.075rem;
    padding: 1rem 0rem;

    ${mobile} {
      font-size: 1rem;
      font-style: normal;
      line-height: 1.5rem;
      padding: 0.75rem 0rem;
    }
  }

  ${({ $locale }) =>
    $locale !== "jp" &&
    `
    p:first-of-type::first-letter {
      font-size: 3.5rem;
      color: ${color.H100};
      line-height: 1.14;
      font-family: ${fonts.heading};
      initial-letter: 2;
      padding-right: 1rem;

      ${mobile} {
        padding-right: 0.5rem;
      }
    }
  `}

  li {
    color: #5c5757;
    text-decoration: inherit;
    padding: 0.5rem 0;
  }

  ul,
  ol {
    list-style: none;
    margin-top: 0;

    @media only screen and (max-width: 65rem) {
      margin-top: 1rem;
    }
  }

  ol {
    counter-reset: custom-counter calc(var(--start) - 1);
  }

  ol li {
    counter-increment: custom-counter;
  }

  ol li::before {
    content: counter(custom-counter);
    color: #e30d76;
    font-weight: bold;

    position: absolute;
    left: -2.5rem;
    top: 0;

    transform: translateY(1rem);
  }

  ul {
    margin-left: 2.5rem;
  }

  ul li::before {
    content: "●";
    color: #e30d76;
    font-size: 0.85rem;

    position: absolute;
    left: -2.25rem;
    top: -0.35rem;

    transform: translateY(0.9rem);
  }

  li > p {
    margin: 0;
    padding: 0;
  }

  li > p:first-child:first-letter {
    float: none;
    font-size: inherit;
    color: inherit;
    font-family: inherit;
    padding: 0;
  }

  h2 {
    font-size: 2.5rem;

    ${mobile} {
      font-size: 1.75rem;
    }
  }

  h3 {
    font-size: 1.875rem;
    font-style: normal;
    font-weight: 700;
    line-height: 2.625rem;
    margin: 2rem 0 1rem;

    ${mobile} {
      font-size: 1.5rem;
      line-height: 2rem;
      margin: 1rem 0;
    }
  }

  a > em {
    font-style: italic;
    color: ${color.P100};
    text-decoration: underline;
  }

  em {
    font-style: italic;
    color: ${color.B1100};
  }

  blockquote,
  blockquote > p,
  blockquote > p:first-child:first-letter,
  blockquote em,
  blockquote a {
    padding: 0;
    border: none;
    float: right;
    width: 18.75rem;
    margin: 0.75rem 0rem 1rem 2.25rem;
    z-index: 2;
    position: relative;
    top: 5px;
    letter-spacing: 0;
    font-family: ${fonts.heading};
    font-size: 2rem;
    line-height: 2.5rem;
    color: ${color.S3100};
    border-left: 0.13rem solid ${color.S3100};
    padding-left: 1rem;

    @media only screen and (max-width: 30rem) {
      float: left;
      margin: 0.75rem 0rem 2.25rem 0rem;
      width: 100%;
      font-size: 1.5rem;
      line-height: 1.7rem;
    }

    position: relative;
    top: 5px;
  }

  blockquote {
    quotes: "“" "”" "‘" "’";
  }

  blockquote a {
    text-decoration: underline;
  }

  blockquote em {
    font-style: italic;
  }

  blockquote::after {
    content: close-quote;
  }

  img,
  iframe {
    width: 100%;
    height: auto;
    padding: 2rem 0rem;
  }

  iframe {
    margin: 0 auto;
    text-align: center;
    display: flex;
    justify-content: center;
    min-height: 26rem;
  }

  img {
    padding: 0rem 0rem 1rem;
  }
`;

const BlogAvatarContainer = styled.div`
  transform: translate(0, 0.66rem);
`;

const BlogAvatar = styled.img`
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
`;

const Title = styled(H1)`
  text-align: center;

  ${mobile} {
    text-align: left;
    font-size: 2rem;
    line-height: 2.5rem;
  }
`;

const Subtitle = styled(P1)`
  text-align: center;

  ${mobile} {
    padding-top: 0.75rem;
    text-align: left;
    font-size: 1rem;
    line-height: 1.5rem;
  }
`;

const BlogAuthorCard = styled.div`
  padding: 2rem;
  background: ${color.B210};
  border: 0.125rem solid ${color.B230};
  box-sizing: border-box;
  box-shadow: 0.25rem 0.25rem 0 ${color.B220};
  border-radius: 0.5rem;
  margin: 2rem auto;
  transition: 150ms ease-out;

  p {
    ${mobile} {
      font-size: 1rem;
      line-height: 1.5rem;
    }
  }

  &:hover {
    transform: translateY(-0.5rem);
    box-shadow: 0.5rem 0.5rem 0 ${color.B220};
  }
`;

const BlogAuthorWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const AuthorName = styled(P1)`
  padding-top: 0;
`;
